import { ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom-v5-compat';

import { OpSpin } from 'new-components/DLS/OpSpin/OpSpin';

interface WelcomePageGuardProps {
  shouldShowWelcomePage: boolean;
  welcomePagePath: string;
  isLoading?: boolean;
  children?: ReactNode;
}

/**
 * @component
 * @description A generic guard component that redirects to a welcome page when a condition isn't met.
 * This follows React Router patterns and can be used across different apps.
 *
 * Typical usage:
 * - When used as a Route element: renders <Outlet /> when condition is met, redirects otherwise
 * - When used as a wrapper: renders children when condition is met, redirects otherwise
 *
 * @param shouldShowWelcomePage - boolean indicating if the user should see the main app content
 * @param welcomePagePath - path to redirect to when shouldShowWelcomePage is false
 * @param isLoading - boolean indicating if the data is still loading (prevents premature redirects)
 * @param children - optional children to render when shouldShowWelcomePage is true (for non-Outlet usage)
 */
export const WelcomePageGuard = ({
  shouldShowWelcomePage,
  welcomePagePath,
  isLoading = false,
  children,
}: WelcomePageGuardProps) => {
  if (isLoading) {
    return <OpSpin centered />;
  }

  if (shouldShowWelcomePage) {
    return <Navigate to={welcomePagePath} replace />;
  }

  if (children) {
    return children;
  }

  return <Outlet />;
};
